import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, PenTool, Maximize, Minimize } from 'lucide-react';
import CopilotWorkspace from "./pages/CopilotWorkspace";
import GuidelinesDashboard from "./pages/GuidelinesDashboard";

function App() {
  const [activeTab, setActiveTab] = useState('learn');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`전체화면 모드 전환 중 오류가 발생했습니다: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex flex-col">
      {/* 글로벌 네비게이션(Top Bar) */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-[0_0_15px_rgba(79,70,229,0.5)]">
              <span className="text-white font-bold text-lg leading-none">R</span>
            </div>
            <span className="font-bold text-white text-lg tracking-tight">Report Masterclass</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex bg-slate-800/50 p-1 rounded-xl border border-slate-700/50">
              <button
                onClick={() => setActiveTab('learn')}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'learn' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
              >
                <BookOpen size={16} />
                가이드 학습
              </button>
              <button
                onClick={() => setActiveTab('practice')}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'practice' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
              >
                <PenTool size={16} />
                AI 코파일럿 실습
              </button>
            </div>
            
            {/* 전체화면 토글 버튼 */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-slate-800/50 rounded-xl border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/80 transition-all duration-200 shadow-sm flex items-center justify-center"
              title={isFullscreen ? "전체화면 종료" : "전체화면"}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 영역 */}
      <main className="flex-1 relative">
        <AnimatePresence mode="wait">
          {activeTab === 'learn' ? (
            <motion.div
              key="learn"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 overflow-auto"
            >
              <GuidelinesDashboard />
            </motion.div>
          ) : (
            <motion.div
              key="practice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 overflow-auto"
            >
              <CopilotWorkspace />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer (권한 명시) */}
      <footer className="py-3 text-center border-t border-slate-800 bg-slate-900/50 z-40">
        <p className="text-slate-500 text-xs tracking-wide">
          본 웹페이지에 대한 모든 권한은 <span className="font-semibold text-slate-400">SY AI LAB</span>에 있습니다.
        </p>
      </footer>
    </div>
  );
}

export default App;
